import {
    type Faro,
} from '@grafana/faro-web-sdk'
import type { createSwapContextWriter } from './faro-session-context'

// Keep this identity aligned with the existing Grafana Faro app configuration.
const FARO_APP_NAME = 'layerswap-frontend'
// Same fallback the widget uses when NEXT_PUBLIC_LS_API is unset.
const DEFAULT_API_URL = 'https://api.layerswap.io'

let faroClient: Faro | undefined
let initializationAttempted = false
const walletContextWriters = new WeakMap<Faro, (attributes: Record<string, string>) => boolean>()
let writeSwapContext: ReturnType<typeof createSwapContextWriter> | undefined

// The provider-free timeline renders synthetic swaps and must never start a telemetry session.
function isTimelinePreview(): boolean {
    return typeof window !== 'undefined' && /(?:^|\/)timeline\/?$/.test(window.location?.pathname || '')
}

function getTracePropagationUrls(): RegExp[] | undefined {
    const configuredUrls = process.env.NEXT_PUBLIC_FARO_TRACE_PROPAGATION_URLS
        ?.split(',')
        .map(url => url.trim())
        .filter(Boolean)

    return configuredUrls?.length
        ? configuredUrls.map(url => {
            const prefix = url.replace(/\/+$/, '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
            return new RegExp(`^${prefix}(?:/|$)`)
        })
        : undefined
}

/**
 * Initializes browser observability once. This is intentionally called from
 * `instrumentation-client.ts`, before the application hydrates, so startup
 * errors and console output are captured too.
 */
export function initFaro(): Faro | undefined {
    if (typeof window === 'undefined' || isTimelinePreview()) return undefined
    if (faroClient) return faroClient

    // The SDK probes storage during module evaluation. Load it only after the preview
    // guard, while keeping instrumentation-client initialization synchronous.
    const { getInternalFaroFromGlobalObject, getWebInstrumentations, initializeFaro, InternalLoggerLevel, PersistentSessionsManager } = require('@grafana/faro-web-sdk') as typeof import('@grafana/faro-web-sdk')
    const existingClient = getInternalFaroFromGlobalObject()
    if (existingClient) {
        faroClient = existingClient
        return faroClient
    }

    if (initializationAttempted) return undefined
    initializationAttempted = true

    const axiomEnabled = process.env.NEXT_PUBLIC_TELEMETRY_PROVIDER === 'axiom'
    const localLogging = process.env.NODE_ENV === 'development' && !axiomEnabled
    const collectorUrl = axiomEnabled
        ? `${window.location.origin}${process.env.NEXT_PUBLIC_AXIOM_TELEMETRY_PATH || '/api/axiom'}`
        : localLogging
        ? `${window.location.origin}${process.env.NEXT_PUBLIC_LOCAL_TELEMETRY_PATH || '/api/local-telemetry'}`
        : process.env.NEXT_PUBLIC_FARO_COLLECTOR_URL
    if (!collectorUrl) {
        if (process.env.NODE_ENV !== 'production') {
            console.warn('[Faro] NEXT_PUBLIC_FARO_COLLECTOR_URL is not set; browser telemetry is disabled.')
        }
        return undefined
    }

    const { ReactIntegration } = require('@grafana/faro-react') as typeof import('@grafana/faro-react')
    const { TracingInstrumentation } = require('@grafana/faro-web-tracing') as typeof import('@grafana/faro-web-tracing')
    const { beforeSend, serializeConsoleArgs } = require('./faro-sanitizer') as typeof import('./faro-sanitizer')
    const { SwapContextInstrumentation } = require('./faro-session-context') as typeof import('./faro-session-context')
    const { getFaroVolumePolicy, createRequestTelemetryFilter } = require('./faro-policy') as typeof import('./faro-policy')
    const { getSessionTrackingConfig } = require('./faro-sampling') as typeof import('./faro-sampling')
    const tracePropagationUrls = getTracePropagationUrls()
    const volumePolicy = getFaroVolumePolicy(process.env.NODE_ENV)
    const filterRequestTelemetry = createRequestTelemetryFilter([
        window.location.origin,
        process.env.NEXT_PUBLIC_LS_API || DEFAULT_API_URL,
        ...(process.env.NEXT_PUBLIC_FARO_TRACE_PROPAGATION_URLS?.split(',').map(url => url.trim()) ?? []),
    ])

    try {
        faroClient = initializeFaro({
            url: collectorUrl,
            app: {
                name: FARO_APP_NAME,
                version: process.env.NEXT_PUBLIC_FARO_RELEASE || 'unknown-release',
                release: process.env.NEXT_PUBLIC_FARO_RELEASE || 'unknown-release',
                // Retain the existing API-mode field for saved queries/links.
                // Deployment identity is separate, immutable page metadata below.
                environment: process.env.NEXT_PUBLIC_API_VERSION === 'testnet' ? 'testnet' : 'mainnet',
            },
            beforeSend: item => {
                if (isTimelinePreview()) return null
                const kept = localLogging ? item : filterRequestTelemetry(item)
                return kept && beforeSend(kept)
            },
            ...volumePolicy,
            // Flush promptly for tail -f while retaining SDK batching and unload handling.
            ...(localLogging ? { batching: { sendTimeout: 1_000 }, logArgsSerializer: serializeConsoleArgs } : {}),
            consoleInstrumentation: {
                ...volumePolicy.consoleInstrumentation,
                // console.error objects are redacted by key before Faro flattens
                // them into one message; beforeSend only sees the text afterwards.
                errorSerializer: serializeConsoleArgs,
            },
            internalLoggerLevel: process.env.NEXT_PUBLIC_FARO_DEBUG === 'true'
                ? InternalLoggerLevel.VERBOSE
                : InternalLoggerLevel.ERROR,
            pageTracking: {
                generatePageId: location => location.pathname,
                page: {
                    attributes: {
                        deployment_environment: process.env.NEXT_PUBLIC_FARO_DEPLOYMENT || 'unknown',
                    },
                },
            },
            sessionTracking: (localLogging || axiomEnabled)
                // Keep lifecycle counts complete in Axiom, including previously unsampled sessions.
                ? { enabled: true, persistent: true, sampler: () => 1 }
                : getSessionTrackingConfig(process.env.NEXT_PUBLIC_FARO_SAMPLE_RATE, () => PersistentSessionsManager.fetchUserSession()),
            experimental: {
                trackNavigation: true,
            },
            instrumentations: [
                new SwapContextInstrumentation(writer => { writeSwapContext = writer }),
                ...getWebInstrumentations({
                    captureConsole: true,
                    enableContentSecurityPolicyInstrumentation: true,
                    enablePerformanceInstrumentation: true,
                }),
                new ReactIntegration(),
                new TracingInstrumentation({
                    instrumentationOptions: tracePropagationUrls
                        ? { propagateTraceHeaderCorsUrls: tracePropagationUrls }
                        : undefined,
                    omitTraceContextForUnsampledSessions: true,
                }),
            ],
        })

        if (localLogging) {
            faroClient?.unpatchedConsole.info('[Telemetry] Writing browser logs to apps/bridge/.next/local-logs/browser.jsonl')
        }
        else if (process.env.NEXT_PUBLIC_FARO_DEBUG === 'true') {
            faroClient?.unpatchedConsole.info('[Faro] Browser telemetry initialized.')
        }
    }
    catch (error) {
        // Observability must never prevent the application from starting.
        console.error('[Faro] Browser telemetry initialization failed.', error)
    }

    return faroClient
}

export function getFaro(): Faro | undefined {
    if (typeof window === 'undefined' || isTimelinePreview()) return undefined
    if (faroClient) return faroClient
    const { getInternalFaroFromGlobalObject } = require('@grafana/faro-web-sdk') as typeof import('@grafana/faro-web-sdk')
    return getInternalFaroFromGlobalObject()
}

/** Returns true when the exception was accepted by Faro. */
export function captureException(error: unknown, context?: Record<string, unknown>): boolean {
    const client = getFaro() ?? initFaro()
    if (!client) return false

    try {
        const { flattenContext } = require('./faro-sanitizer') as typeof import('./faro-sanitizer')
        client.api.pushError(error instanceof Error ? error : new Error(String(error)), {
            context: context ? flattenContext(context) : undefined,
        })
        return true
    }
    catch (captureError) {
        client.unpatchedConsole.error('[Faro] Failed to capture exception.', captureError)
        return false
    }
}

/** Sends a structured business event using the field layout expected by the Faro dashboards. */
export function captureEvent(name: string, attributes?: Record<string, unknown>): boolean {
    const client = getFaro() ?? initFaro()
    if (!client) return false

    try {
        const { flattenContext } = require('./faro-sanitizer') as typeof import('./faro-sanitizer')
        client.api.pushEvent(name, attributes ? flattenContext(attributes) : undefined)
        return true
    }
    catch (captureError) {
        client.unpatchedConsole.error(`[Faro] Failed to capture event "${name}".`, captureError)
        return false
    }
}

/** Adds the active swap to every subsequent Faro signal without rotating its session ID. */
export function setSwapContext(
    attributes: Record<string, unknown>,
    options: { replaceAttributes?: boolean } = {},
): boolean {
    const client = getFaro() ?? initFaro()
    if (!client) return false

    try {
        const { flattenContext } = require('./faro-sanitizer') as typeof import('./faro-sanitizer')
        const { createSwapContextWriter } = require('./faro-session-context') as typeof import('./faro-session-context')
        writeSwapContext ??= createSwapContextWriter(client.api, listener => client.metas.addListener(listener))
        return writeSwapContext(flattenContext(attributes), options.replaceAttributes ?? false)
    }
    catch (captureError) {
        client.unpatchedConsole.error('[Faro] Failed to set swap session context.', captureError)
        return false
    }
}

/** Replace only wallet-owned fields; do not rotate the session or touch swap data. */
export function setWalletContext(attributes: Record<string, string>): boolean {
    const client = getFaro() ?? initFaro()
    if (!client) return false
    try {
        const { flattenContext } = require('./faro-sanitizer') as typeof import('./faro-sanitizer')
        const { createWalletContextWriter } = require('./faro-session-context') as typeof import('./faro-session-context')
        let write = walletContextWriters.get(client)
        if (!write) {
            write = createWalletContextWriter(client.api, listener => client.metas.addListener(listener))
            walletContextWriters.set(client, write)
        }
        return write(flattenContext(attributes))
    }
    catch {
        // Do not log wallet/provider data or interfere with wallet interactions.
        return false
    }
}

export function setFaroView(name: string): void {
    const client = getFaro() ?? initFaro()
    if (!client) return

    try {
        client.api.setView({ name })
    }
    catch (captureError) {
        // Runs on every route change; observability must never break navigation.
        client.unpatchedConsole.error('[Faro] Failed to set view.', captureError)
    }
}
